import React from 'react';
import { motion } from 'framer-motion';
import { 
  AlertTriangle, 
  Flame, 
  Droplets, 
  Mountain, 
  Wind, 
  Waves,
  Activity,
  Globe,
  RefreshCw
} from 'lucide-react';
import { useAllDisasters } from '../hooks/useAllDisasters';

const DisasterDashboard: React.FC = () => {
  const { disasters, loading, error, lastUpdated } = useAllDisasters(60000);

  const counts = {
    earthquake: disasters.filter(d => d.type === 'earthquake').length,
    flood: disasters.filter(d => d.type === 'flood').length,
    fire: disasters.filter(d => d.type === 'fire').length,
    cyclone: disasters.filter(d => d.type === 'cyclone').length,
    volcano: disasters.filter(d => d.type === 'volcano').length,
    tsunami: disasters.filter(d => d.type === 'tsunami').length,
    drought: disasters.filter(d => d.type === 'drought').length
  };

  const criticalCount = disasters.filter(d => d.severity === 'critical').length;

  const getTypeIcon = (type: string) => {
    switch(type) {
      case 'earthquake': return <Mountain size={16} className="text-orange-500" />;
      case 'flood': return <Droplets size={16} className="text-blue-500" />;
      case 'fire': return <Flame size={16} className="text-red-500" />;
      case 'cyclone': return <Wind size={16} className="text-purple-500" />;
      case 'tsunami': return <Waves size={16} className="text-cyan-500" />;
      case 'volcano': return <Mountain size={16} className="text-red-500" />;
      default: return <Activity size={16} className="text-gray-500" />;
    }
  };

  if (loading && disasters.length === 0) {
    return (
      <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={24} className="animate-spin text-cyan-500 mr-3" />
          <span className="text-gray-400">Fetching global disaster data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Globe className="text-red-500 animate-pulse" />
            GLOBAL DISASTER MONITORING
          </h2>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-1">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
          <p className="text-2xl font-bold text-red-500">{criticalCount}</p>
          <p className="text-xs text-gray-400">Critical</p>
        </div>
        <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
          <p className="text-2xl font-bold text-blue-500">{disasters.length}</p>
          <p className="text-xs text-gray-400">Total Active</p>
        </div>
        <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
          <p className="text-2xl font-bold text-orange-500">{counts.earthquake}</p>
          <p className="text-xs text-gray-400">Earthquakes</p>
        </div>
        <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
          <p className="text-2xl font-bold text-yellow-500">{counts.fire}</p>
          <p className="text-xs text-gray-400">Fires</p>
        </div>
      </div>

      {/* Type Distribution */}
      <div className="grid grid-cols-4 gap-2 mb-6">
        {Object.entries(counts).map(([type, count]) => (
          <div key={type} className="flex items-center gap-2 p-2 bg-black/30 rounded-lg">
            {getTypeIcon(type)}
            <span className="text-xs capitalize flex-1">{type}</span>
            <span className="text-sm font-bold">{count}</span>
          </div>
        ))}
      </div>

      {/* Recent Disasters List */}
      <h3 className="text-sm font-bold mb-3">Recent Disasters</h3>
      <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
        {disasters.slice(0, 20).map((disaster) => (
          <motion.div
            key={disaster.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-3 bg-black/30 rounded-lg border border-gray-700 hover:border-cyan-500/50 transition-all"
          >
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-2">
                {getTypeIcon(disaster.type)}
                <div>
                  <p className="font-medium text-sm">{disaster.title}</p>
                  <p className="text-xs text-gray-400">{disaster.location.name}</p>
                </div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                disaster.severity === 'critical' ? 'bg-red-500/20 text-red-500' :
                disaster.severity === 'high' ? 'bg-orange-500/20 text-orange-500' :
                disaster.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-500' :
                'bg-green-500/20 text-green-500'
              }`}>
                {disaster.severity}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
              <span>{new Date(disaster.timestamp).toLocaleString()}</span>
              <span>•</span>
              <span>{disaster.sources.join(', ')}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
};

export default DisasterDashboard;